import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import {
  AI_AGENT_FIELD_CONSTS,
  CUSTOM_MODEL,
  AI_AGENT_FIELD_NAMES,
} from '../../../ai-agents/proxy/ai-agents.consts';
import {
  LlmConfigurationProviderModelsDto,
  LlmConfigurationSummaryDto,
  ReasoningEffort,
} from '../../../integrations/proxy/integration.model';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { finalize, forkJoin } from 'rxjs';
import { UserLlmConfigurationDto } from 'src/app/ai-agents/proxy/ai-agents.model';
import { IntegrationService } from 'src/app/integrations/proxy/integration.service';
import { ObjectHelper } from 'src/app/shared/helpers/object.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'cai-llm-settings',
  templateUrl: './llm-settings.component.html',
  styleUrl: './llm-settings.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class LlmSettingsComponent implements OnInit, OnChanges {
  @Input() formGroup!: FormGroup;
  @Input() llmConfigurationList: LlmConfigurationSummaryDto[] = [];
  @Input() groupedModelList: LlmConfigurationProviderModelsDto[] = [];
  @Input() isUpdateMode: boolean;
  @Input() enableReasoningEffortSelection: boolean;
  @Input() enableTokenPerMinuteSelection: boolean;
  @Input() disableReasoningEffortSelectionOnCustomModels: boolean;
  @Input() hideHeader = false;
  @Input() isReadOnly = false;

  private _isOptional = false;
  @Input()
  set isOptional(value) {
    this._isOptional = value;

    this.setFormControlsValueAndValidity();
  }
  get isOptional() {
    return this._isOptional;
  }

  @Output() setLlmSettingsRequested = new EventEmitter<UserLlmConfigurationDto>();
  @BlockUI('llm-settings-block-ui') blockUI: NgBlockUI;

  llmModelList = [];
  selectedLlmConfigurationId: string;
  selectedLlmConfiguration: LlmConfigurationSummaryDto;
  hideModelSelection = false;
  customModel = CUSTOM_MODEL;
  aiAgentFieldNames = AI_AGENT_FIELD_NAMES;
  selectedModelSupportsReasoning = false;
  isCustomModelSelected = false;
  enableReasoningEffort = false;

  reasoningEffortLevels = ObjectHelper.getEnumValues(ReasoningEffort);

  constructor(
    private integrationService: IntegrationService,
    private destroyRef: DestroyRef,
  ) {
    this.blockUI.start();
  }

  ngOnInit() {
    if (!this.isUpdateMode) {
      this.integrationService.userLlmSettingsPatchedSubject
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.handleLlmConfigurationChange();
        });

      forkJoin({
        llmConfigurations: this.integrationService.getAvailableLlmConfigurations(),
        userLlmConfiguration: this.integrationService.getUserLlmConfiguration(),
        suggestedModels: this.integrationService.getSuggestedModels(),
      })
        .pipe(
          finalize(() => {
            this.blockUI.stop();
          }),
        )
        .subscribe(({ llmConfigurations, userLlmConfiguration, suggestedModels }) => {
          this.llmConfigurationList = llmConfigurations;

          if (this.groupedModelList.length === 0) {
            this.groupedModelList = suggestedModels;
          }

          // Set last used LLM configuration as default
          if (
            !this.formGroup.get(this.aiAgentFieldNames.llmConfiguration).value &&
            userLlmConfiguration
          ) {
            this.setLlmSettingsRequested.emit(userLlmConfiguration);
          } else {
            this.handleLlmConfigurationChange();
          }
        });
    } else {
      // In update mode, suggestedModels should be provided as input
      this.handleLlmConfigurationChange(true);
    }

    this.formGroup
      .get(AI_AGENT_FIELD_NAMES.llmModel)
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((model: string) => {
        const wasCustomModel = this.isCustomModelSelected;
        this.isCustomModelSelected = model === CUSTOM_MODEL;
        const providerType = Number(this.selectedLlmConfiguration?.provider);
        const currentReasoningEffort = this.formGroup.get(
          AI_AGENT_FIELD_NAMES.reasoningEffort,
        ).value;

        if (model === CUSTOM_MODEL) {
          this.formGroup
            .get(AI_AGENT_FIELD_NAMES.llmCustomModel)
            .setValidators([
              Validators.required,
              Validators.maxLength(AI_AGENT_FIELD_CONSTS.llmModelMaxLength),
            ]);

          // When switching to custom model, reset reasoning effort to ensure default behavior
          // Unless it's already been explicitly set (not null and not just the default disable value from handleReasoningEffort)
          if (currentReasoningEffort === null) {
            this.resetReasoningEffortState();
          }
        } else {
          this.formGroup.get(AI_AGENT_FIELD_NAMES.llmCustomModel).clearValidators();

          // Only reset reasoning effort when switching FROM custom model TO regular model
          // Don't reset when switching between regular models (e.g., during Apply to All)
          if (wasCustomModel && currentReasoningEffort !== null) {
            this.resetReasoningEffortState();
          }
        }

        this.handleReasoningEffort(providerType, model);
        this.formGroup.get(AI_AGENT_FIELD_NAMES.llmCustomModel).updateValueAndValidity();
      });

    this.formGroup
      .get(AI_AGENT_FIELD_NAMES.llmConfiguration)
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formGroup.get(AI_AGENT_FIELD_NAMES.llmModel).reset();
        this.formGroup.get(AI_AGENT_FIELD_NAMES.llmCustomModel).reset();

        this.handleLlmConfigurationChange();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Workaround to re-render the tool edit component.
    if (changes.llmConfigurationList || changes.groupedModelList) {
      this.handleLlmConfigurationChange(true);
    }

    // Stop blockUI when in update mode and inputs have been received (even if empty)
    if (
      this.isUpdateMode !== undefined &&
      (changes.llmConfigurationList || changes.groupedModelList || changes.isUpdateMode)
    ) {
      this.blockUI.stop();
    }
  }

  private handleLlmConfigurationChange(isInitialLoad = false) {
    this.setFormControlsValueAndValidity();

    if (!this.llmConfigurationList) {
      return;
    }

    const llmConfigurationId = this.formGroup.get(AI_AGENT_FIELD_NAMES.llmConfiguration).value;
    if (!llmConfigurationId) {
      this.llmModelList = [];
      this.hideModelSelection = false;

      return;
    }

    this.selectedLlmConfiguration = this.llmConfigurationList.find(
      (x) => x.id === llmConfigurationId,
    );

    if (!this.selectedLlmConfiguration) {
      return;
    }

    const providerType = Number(this.selectedLlmConfiguration?.provider);
    this.llmModelList =
      this.groupedModelList
        ?.find((gm) => gm.provider === providerType)
        ?.models?.filter((m) => !m.isLegacy)
        .map((m) => m.name) ?? [];
    this.hideModelSelection = !this.llmModelList.length;

    if (this.hideModelSelection) {
      this.formGroup.get(AI_AGENT_FIELD_NAMES.llmModel).clearValidators();

      this.formGroup
        .get(AI_AGENT_FIELD_NAMES.llmModel)
        .updateValueAndValidity({ emitEvent: false });
    } else {
      // If there is only one model available, select it by default
      if (this.llmModelList.length === 1) {
        const modelValue = this.llmModelList[0];

        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.llmModel)
          .patchValue(modelValue, { emitEvent: false });
      }

      this.formGroup.get(AI_AGENT_FIELD_NAMES.llmModel).setValidators([Validators.required]);
      this.formGroup
        .get(AI_AGENT_FIELD_NAMES.llmModel)
        .updateValueAndValidity({ emitEvent: false });

      // Handle custom model validation after setting the value
      const llmModelValue = this.formGroup.get(AI_AGENT_FIELD_NAMES.llmModel).value;
      if (llmModelValue === CUSTOM_MODEL) {
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.llmCustomModel)
          .setValidators([
            Validators.required,
            Validators.maxLength(AI_AGENT_FIELD_CONSTS.llmModelMaxLength),
          ]);

        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.llmCustomModel)
          .updateValueAndValidity({ emitEvent: false });
      }

      this.handleReasoningEffort(providerType, llmModelValue, isInitialLoad);
    }
  }

  private setFormControlsValueAndValidity() {
    if (this.isOptional) {
      this.formGroup
        .get(AI_AGENT_FIELD_NAMES.llmConfiguration)
        .removeValidators([Validators.required]);
      this.formGroup
        .get(AI_AGENT_FIELD_NAMES.llmConfiguration)
        .updateValueAndValidity({ emitEvent: false });

      const llmConfigurationId = this.formGroup.get(AI_AGENT_FIELD_NAMES.llmConfiguration).value;

      if (!llmConfigurationId) {
        this.formGroup.get(AI_AGENT_FIELD_NAMES.llmModel).removeValidators([Validators.required]);
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.llmModel)
          .updateValueAndValidity({ emitEvent: false });

        if (this.enableTokenPerMinuteSelection) {
          this.formGroup
            .get(AI_AGENT_FIELD_NAMES.tokenPerMinute)
            .removeValidators([Validators.required]);
          this.formGroup
            .get(AI_AGENT_FIELD_NAMES.tokenPerMinute)
            .updateValueAndValidity({ emitEvent: false });
        }
      } else {
        if (this.enableTokenPerMinuteSelection) {
          this.formGroup
            .get(AI_AGENT_FIELD_NAMES.tokenPerMinute)
            .addValidators([Validators.required]);
          this.formGroup
            .get(AI_AGENT_FIELD_NAMES.tokenPerMinute)
            .updateValueAndValidity({ emitEvent: false });
        }
      }
    } else {
      this.formGroup
        .get(AI_AGENT_FIELD_NAMES.llmConfiguration)
        .addValidators([Validators.required]);
      this.formGroup
        .get(AI_AGENT_FIELD_NAMES.llmConfiguration)
        .updateValueAndValidity({ emitEvent: false });

      if (this.enableTokenPerMinuteSelection) {
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.tokenPerMinute)
          .addValidators([Validators.required]);
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.tokenPerMinute)
          .updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  enableReasoningEffortChange(value: boolean) {
    if (value) {
      this.formGroup.get(AI_AGENT_FIELD_NAMES.reasoningEffort).setValue(ReasoningEffort.none);
    } else {
      this.resetReasoningEffortState();
    }
  }

  resetReasoningEffortState() {
    this.formGroup.get(AI_AGENT_FIELD_NAMES.reasoningEffort).setValue(null);
    this.formGroup.get(AI_AGENT_FIELD_NAMES.reasoningEffort).clearValidators();
    this.formGroup
      .get(AI_AGENT_FIELD_NAMES.reasoningEffort)
      .updateValueAndValidity({ emitEvent: false });
  }

  private handleReasoningEffort(providerType: number, llmModelValue: any, isInitialLoad = false) {
    const model = this.groupedModelList
      .find((sm) => sm.provider == providerType)
      ?.models.find((m) => m.name == llmModelValue);

    this.selectedModelSupportsReasoning = model?.supportsReasoning ?? false;
    this.isCustomModelSelected = llmModelValue === CUSTOM_MODEL;
    const currentReasoningEffortValue = this.formGroup.get(
      AI_AGENT_FIELD_NAMES.reasoningEffort,
    ).value;
    this.enableReasoningEffort =
      llmModelValue === CUSTOM_MODEL && currentReasoningEffortValue !== null;

    if (isInitialLoad) return;

    // Reset reasoning effort if the model doesn't support reasoning or existing reasoning effort has no value
    // Use !== null check instead of truthy check because ReasoningEffort.none is 0 (falsy)
    this.updateReasoningEffort(llmModelValue, currentReasoningEffortValue);
  }

  private updateReasoningEffort(llmModelValue: any, currentReasoningEffortValue: any) {
    const shouldHaveReasoningEffort =
      (llmModelValue === CUSTOM_MODEL && currentReasoningEffortValue !== null) ||
      this.selectedModelSupportsReasoning;

    if (!shouldHaveReasoningEffort) {
      this.resetReasoningEffortState();
    } else {
      // Only set default value and validators if model supports reasoning (not for custom models)
      // For custom models, user needs to explicitly enable reasoning effort via the toggle
      if (this.selectedModelSupportsReasoning) {
        if (currentReasoningEffortValue === null) {
          this.formGroup.get(AI_AGENT_FIELD_NAMES.reasoningEffort).setValue(ReasoningEffort.none);
        }
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.reasoningEffort)
          .setValidators([Validators.required]);
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.reasoningEffort)
          .updateValueAndValidity({ emitEvent: false });
      } else if (llmModelValue === CUSTOM_MODEL && currentReasoningEffortValue !== null) {
        // For custom models with explicitly set reasoning effort, set validators
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.reasoningEffort)
          .setValidators([Validators.required]);
        this.formGroup
          .get(AI_AGENT_FIELD_NAMES.reasoningEffort)
          .updateValueAndValidity({ emitEvent: false });
      }
    }
  }
}
